import React, { useEffect, useRef, useState } from 'react';
import { time } from './EventAdminPanel';

const duration = (value) =>
    value === null || value === undefined ? '-' : value < 60000 ? Math.round(value / 1000) + '초' : Math.floor(value / 60000) + '분 ' + Math.round((value % 60000) / 1000) + '초';
const percent = (value) => (value === null || value === undefined ? '-' : Math.round(value * 100) + '%');
const sources = { all: '전체', onsite: '현장 체험', shared: '공유로 체험', shared_view: '공유 결과 열람' };
const devices = { all: '전체', phone: '휴대폰', tablet: '태블릿', desktop: 'PC' };
const screens = {
    landing: '이벤트 상세',
    gender: '성별',
    birth: '출생 연도',
    hobby: '취미',
    style: '프로필 종류',
    photo: '사진 선택',
    crop: '사진 위치 조절',
    result: '프로필 결과',
    shared: '공유 결과',
    unavailable: '접속 오류',
    generation_error: '생성 오류',
    generation_wait: '생성 대기',
    terms: '약관',
    warning: '사진 확인',
    'birth-picker': '출생 연도 선택',
    'photo-guide': '사진 안내',
    'share-options': '공유',
    'photo-preview': '사진 확대',
};
const actions = {
    start: '체험 시작',
    home: '처음으로',
    next: '다음',
    back: '이전',
    download: '사진 저장 시도',
    share: '공유 열기',
    'share-copy': '공유 링크 복사',
    'share-device': '기기 공유',
    'share-kakao': '카카오 공유',
    'share-qr': 'QR 공유',
    print: '출력 요청',
    remake: '다시 만들기',
    retry: '생성 재시도',
    'gender-select': '성별 선택',
    'hobby-select': '취미 선택',
    'style-select': '스타일 선택',
    'choose-photo': '사진 변경',
    'photo-selected': '사진 선택 완료',
    'crop-done': '사진 위치 확정',
    'modal-dismiss': '모달 닫기',
    'consent-next': '동의 후 다음',
    'media-retry': '이미지 재시도',
};
const operations = {
    config: '화면 설정',
    participant: '참여 등록',
    details: '정보 변경',
    generation: 'AI 생성',
    cloud_upload: '사진 업로드',
    share: '공유',
    print: '출력 요청',
    media: '이미지 불러오기',
    photo: '사진 읽기',
    runtime: '화면 실행',
};
const states = { active: '진행 중', abandoned: '이탈 추정', completed: '결과 확인' };

function Table({ headers, rows }) {
    return rows.length ? (
        <div className="ai-table">
            <table>
                <thead>
                    <tr>
                        {headers.map((label) => (
                            <th key={label}>{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index}>
                            {row.map((value, column) => (
                                <td key={column}>{value}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ) : (
        <p>조회 기간의 이용 기록이 없어요.</p>
    );
}

export default function EventAnalyticsPanel({ api }) {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
    const [filters, setFilters] = useState({ from: today, to: today, source: 'all', device: 'all' });
    const [query, setQuery] = useState({ from: today, to: today, source: 'all', device: 'all', page: 1, revision: 0 });
    const [report, setReport] = useState(null);
    const [view, setView] = useState('funnel');
    const [detail, setDetail] = useState(null);
    const [notice, setNotice] = useState('');
    const detailRequest = useRef(null);
    useEffect(() => {
        const controller = new AbortController();
        const { revision, ...parameters } = query;
        setNotice('분석 기록 불러오는 중');
        api('analytics?' + new URLSearchParams(parameters), undefined, 'GET', { signal: controller.signal })
            .then((value) => {
                if (controller.signal.aborted) return;
                setReport(value);
                setNotice(value.truncated ? '기록이 5,000세션을 초과했어요. 조회 기간을 줄여 주세요.' : '');
            })
            .catch((error) => {
                if (!controller.signal.aborted) setNotice(error.response?.data?.error || '분석 기록을 불러오지 못했어요.');
            });
        return () => controller.abort();
    }, [api, query]);
    useEffect(() => () => detailRequest.current?.abort(), []);
    async function session(key) {
        detailRequest.current?.abort();
        const controller = new AbortController();
        detailRequest.current = controller;
        setDetail(null);
        try {
            const value = await api('analytics-session?key=' + encodeURIComponent(key), undefined, 'GET', { signal: controller.signal });
            if (!controller.signal.aborted) setDetail(value);
        } catch (error) {
            if (!controller.signal.aborted) setNotice(error.response?.data?.error || '세션 기록을 불러오지 못했어요.');
        }
    }
    const update = (event) => setFilters({ ...filters, [event.target.name]: event.target.value });
    const s = report?.summary;
    const eventLabel = (event) =>
        event.type === 'view'
            ? '화면 진입'
            : event.type === 'action'
              ? actions[event.action] || event.action
              : event.type === 'generation_status'
                ? '생성 상태 · ' + ({ queued: '대기', generating: '생성 중', completed: '완료', failed: '실패' }[event.state] || event.state)
                : `${operations[event.operation] || event.operation} · ${{ success: '성공', failure: '실패', cancel: '취소' }[event.outcome] || event.outcome}${event.code && event.code !== 'none' ? ' · ' + event.code : ''}`;
    return (
        <section>
            <h2>이용 분석</h2>
            <small>한국 시간 · 기록 보관 30일 · 사람 수가 아닌 이용 회차입니다.</small>
            <form
                className="ai-toolbar"
                onSubmit={(event) => {
                    event.preventDefault();
                    setQuery({ ...filters, page: 1, revision: query.revision + 1 });
                }}
            >
                <label>
                    시작일
                    <input type="date" name="from" required value={filters.from} onChange={update} />
                </label>
                <label>
                    종료일
                    <input type="date" name="to" required value={filters.to} onChange={update} />
                </label>
                <label>
                    유입
                    <select name="source" value={filters.source} onChange={update}>
                        {Object.entries(sources).map(([key, value]) => (
                            <option key={key} value={key}>
                                {value}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    기기
                    <select name="device" value={filters.device} onChange={update}>
                        {Object.entries(devices).map(([key, value]) => (
                            <option key={key} value={key}>
                                {value}
                            </option>
                        ))}
                    </select>
                </label>
                <button type="submit">조회·새로고침</button>
            </form>
            <p role="status">{notice}</p>
            {s && (
                <div className="ai-summary">
                    {[
                        ['방문 세션', s.sessions],
                        ['체험 시작', s.started],
                        ['시작 후 결과 확인', s.completed],
                        ['완성률', percent(s.completionRate)],
                        ['완성 소요 중앙값', duration(s.medianCompletionMs)],
                        ['진행 중', s.active],
                        ['이탈 추정', s.abandoned],
                        ['오류 경험 세션', s.errorSessions],
                    ].map(([label, value]) => (
                        <div className="ai-stat" key={label}>
                            <small>{label}</small>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>
            )}
            <nav aria-label="이용 분석 보기">
                {[
                    ['funnel', '단계별 퍼널'],
                    ['screens', '화면·스크롤'],
                    ['actions', '버튼 클릭'],
                    ['errors', '오류'],
                    ['sessions', '개별 이용 기록'],
                ].map(([key, label]) => (
                    <button type="button" key={key} aria-pressed={view === key} onClick={() => setView(key)}>
                        {label}
                    </button>
                ))}
            </nav>
            {report && (
                <>
                    {view === 'funnel' &&
                        Object.entries(report.funnels)
                            .filter(([source]) => query.source === 'all' || query.source === source)
                            .map(([source, stages]) => (
                                <section key={source}>
                                    <h3>{sources[source]}</h3>
                                    <Table
                                        headers={['단계', '도달 세션', '이전 단계 전환율', '이탈 추정']}
                                        rows={stages.map((row) => [
                                            row.label,
                                            <div>
                                                {row.reached}
                                                <meter min="0" max={stages[0].reached || 1} value={row.reached} aria-label={row.label + ' 도달 비율'} />
                                            </div>,
                                            percent(row.conversion),
                                            row.drop,
                                        ])}
                                    />
                                </section>
                            ))}
                    {view === 'screens' && (
                        <Table
                            headers={['화면', '도달 세션', '활성 체류 중앙값', '활성 체류 평균', '최대 스크롤 중앙값', '마지막 스크롤 중앙값', '마지막 화면 이탈']}
                            rows={report.screens.map((row) => [
                                screens[row.screen] || row.screen,
                                row.sessions,
                                duration(row.medianActiveMs),
                                duration(row.meanActiveMs),
                                row.medianMaxDepth === null ? '-' : row.medianMaxDepth + '%',
                                row.medianLastDepth === null ? '-' : row.medianLastDepth + '%',
                                row.abandoned,
                            ])}
                        />
                    )}
                    {view === 'actions' && (
                        <Table
                            headers={['화면', '버튼·선택', '총 횟수', '사용 세션']}
                            rows={report.clicks.map((row) => [screens[row.screen] || row.screen, actions[row.action] || row.action, row.count, row.sessions])}
                        />
                    )}
                    {view === 'errors' && (
                        <Table
                            headers={['화면', '작업', '오류 코드', '발생 횟수', '경험 세션', '이후 같은 작업 성공']}
                            rows={report.errors.map((row) => [screens[row.screen] || row.screen, operations[row.operation] || row.operation, row.code, row.count, row.sessions, row.recovered])}
                        />
                    )}
                    {view === 'sessions' && (
                        <>
                            <Table
                                headers={['시작', '세션', '유입·기기', '상태', '마지막 화면', '활성 체류', '결과까지', '오류', '상세']}
                                rows={report.sessions.map((row) => [
                                    time(row.startedAt),
                                    row.key.slice(-8),
                                    `${sources[row.source]} · ${devices[row.device]} · ${row.os}`,
                                    states[row.status],
                                    screens[row.lastScreen] || row.lastScreen,
                                    duration(row.activeMs),
                                    duration(row.completionMs),
                                    row.errors,
                                    <button type="button" onClick={() => session(row.key)}>
                                        상세
                                    </button>,
                                ])}
                            />
                            <div className="ai-actions">
                                <button type="button" disabled={report.page <= 1} onClick={() => setQuery({ ...query, page: report.page - 1 })}>
                                    이전
                                </button>
                                <span>
                                    {report.page} / {report.pages}
                                </span>
                                <button type="button" disabled={report.page >= report.pages} onClick={() => setQuery({ ...query, page: report.page + 1 })}>
                                    다음
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
            {detail && (
                <div className="ai-modal" role="presentation" onClick={() => setDetail(null)}>
                    <section className="ai-dialog" role="dialog" aria-modal="true" aria-label="개별 이용 기록" onClick={(event) => event.stopPropagation()}>
                        <button type="button" onClick={() => setDetail(null)}>
                            닫기
                        </button>
                        <h2>세션 {detail.key.slice(-8)}</h2>
                        <p>
                            {time(detail.startedAt)} · {sources[detail.source]} · {devices[detail.device]} · {detail.os}
                        </p>
                        <p>
                            {states[detail.status]} · 전체 경과 {duration(detail.elapsedMs)} · 활성 체류 {duration(detail.activeMs)} · 결과까지 {duration(detail.completionMs)}
                        </p>
                        <h3>화면별 기록</h3>
                        <Table
                            headers={['화면', '활성 체류', '최대 스크롤', '마지막 스크롤', '마지막 영역']}
                            rows={detail.visits.map((visit) => [
                                screens[visit.screen] || visit.screen,
                                duration(visit.activeMs),
                                visit.scrollable ? visit.maxDepth + '%' : '-',
                                visit.scrollable ? visit.lastDepth + '%' : '-',
                                visit.section === 'none' ? '-' : visit.section,
                            ])}
                        />
                        <h3>시간순 이용 기록</h3>
                        <Table
                            headers={['경과', '화면', '행동·작업']}
                            rows={detail.events.filter((event) => event.type !== 'progress').map((event) => [duration(event.at), screens[event.screen] || event.screen, eventLabel(event)])}
                        />
                    </section>
                </div>
            )}
        </section>
    );
}
